package com.sddamico.swigmore.util

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlin.js.Promise

data class BrowserPushSubscription(
  val endpoint: String,
  val p256dh: String,
  val auth: String,
)

fun isPushSupported(): Boolean =
  js("('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window)") as Boolean

fun notificationPermission(): String =
  js("typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'") as String

suspend fun ensureServiceWorker() {
  (js("navigator.serviceWorker.register('/sw.js')") as Promise<dynamic>).await()
  (js("navigator.serviceWorker.ready") as Promise<dynamic>).await()
}

suspend fun currentPushSubscription(): BrowserPushSubscription? {
  return try {
    val registration = (js("navigator.serviceWorker.ready") as Promise<dynamic>).await()
    val subscription = (registration.pushManager.getSubscription() as Promise<dynamic>).await()
      ?: return null
    subscriptionToData(subscription)
  } catch (_: Throwable) {
    null
  }
}

/** Thrown when the browser denied/blocked the notification permission (not an error to surface loudly). */
class PushPermissionDeniedException(val permission: String) : Exception("Notification permission: $permission")

/**
 * Subscribes the active service worker to Web Push. Call this synchronously from a user-gesture
 * handler with NO awaits before it — Android Chrome invalidates the transient user activation across
 * an intervening network await, which makes requestPermission()/subscribe() silently fail on mobile.
 * Errors propagate so the caller can surface the real reason (desktop console was the only signal before).
 */
suspend fun subscribeToPush(vapidPublicKey: String): BrowserPushSubscription {
  val permission = (js("Notification.requestPermission()") as Promise<dynamic>).await() as String
  if (permission != "granted") throw PushPermissionDeniedException(permission)

  val registration = (js("navigator.serviceWorker.ready") as Promise<dynamic>).await()
  val options = js("({})")
  options.userVisibleOnly = true
  options.applicationServerKey = urlBase64ToUint8Array(vapidPublicKey)
  val subscription = (registration.pushManager.subscribe(options) as Promise<dynamic>).await()
  return subscriptionToData(subscription)
}

/**
 * This browser's live subscription, or null. Reads the registration with `getRegistration()` rather
 * than `serviceWorker.ready`, which never settles on a browser that was never enrolled — sign-out
 * runs through here and must not hang there.
 */
private suspend fun existingSubscription(): dynamic {
  if (!isPushSupported()) return null
  return try {
    val registration = (js("navigator.serviceWorker.getRegistration()") as Promise<dynamic>).await()
      ?: return null
    (registration.pushManager.getSubscription() as Promise<dynamic>).await()
  } catch (_: Throwable) {
    null
  }
}

/** The endpoint the server knows this browser by, for un-enrolling an account that is leaving it. */
suspend fun currentPushEndpoint(): String? = existingSubscription()?.endpoint as String?

/** Drops this browser's subscription at the push provider. Safe to call when there is none. */
suspend fun releasePushSubscription() {
  try {
    val subscription = existingSubscription() ?: return
    (subscription.unsubscribe() as Promise<dynamic>).await()
  } catch (_: Throwable) {
  }
}

private fun subscriptionToData(subscription: dynamic): BrowserPushSubscription {
  val json = subscription.toJSON()
  return BrowserPushSubscription(
    endpoint = subscription.endpoint as String,
    p256dh = json.keys.p256dh as String,
    auth = json.keys.auth as String,
  )
}

private fun urlBase64ToUint8Array(base64String: String): dynamic {
  val padding = "=".repeat((4 - base64String.length % 4) % 4)
  val base64 = (base64String + padding).replace("-", "+").replace("_", "/")
  val rawData = window.atob(base64)
  val output = js("new Uint8Array(rawData.length)")
  for (i in 0 until rawData.length) output[i] = rawData.asDynamic().charCodeAt(i)
  return output
}
